import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { Loader2, User, ChevronLeft } from "lucide-react";

/**
 * iter260 — Admin: Individual users archive (read-only).
 * Strictly separate from the "المستخدمون" (users) tab and from the
 * business archive.
 */
const NA = "غير متوفر تاريخيًا";

function fmt(dt) {
  if (!dt) return NA;
  try {
    const d = new Date(dt);
    return d.toLocaleString("ar-SA", { hour12: false });
  } catch {
    return NA;
  }
}

export default function IndividualArchiveTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await apiClient.get("/admin/archive/individuals", { params: { q, city } });
      setRows(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [q, city]);

  useEffect(() => { load(); }, [load]);

  const open = async (uid) => {
    setSelected(uid); setDetail(null);
    try {
      const { data } = await apiClient.get(`/admin/archive/individuals/${uid}`);
      setDetail(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  if (selected && detail) {
    const { user, timeline, listings, where } = detail;
    return (
      <div className="space-y-4" data-testid="individual-detail">
        <button onClick={() => { setSelected(null); setDetail(null); }} className="btn-ghost text-xs">
          <ChevronLeft className="w-4 h-4" /> العودة
        </button>
        <div className="surface rounded-xl p-4">
          <h3 className="font-display text-xl mb-2">{user.name}</h3>
          <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#3a3a3a]">
            <div>البريد: {user.email}</div>
            <div>الجوال: {user.phone || NA}</div>
            <div>المدينة: {user.city || NA}</div>
            <div>نوع الحساب: {user.role}</div>
            <div>إنشاء الحساب: {fmt(user.created_at)}</div>
            <div>آخر نشاط: {fmt(user.updated_at || user.last_login_at)}</div>
          </div>
        </div>
        <div className="surface rounded-xl p-4">
          <h4 className="font-bold mb-2">أين وصل؟</h4>
          <div className="text-sm grid sm:grid-cols-2 gap-1">
            <div>آخر خطوة مكتملة: {where.last_completed || NA}</div>
            <div>الخطوة الحالية: {where.current_step || NA}</div>
            <div>حالة التسجيل: {where.registration_status}</div>
            <div>حالة التوثيق: {where.verification_status}</div>
            <div>آخر نشاط: {fmt(where.last_activity_at)}</div>
            <div>سبب التوقف: {where.stop_reason || "غير محدد"}</div>
          </div>
        </div>
        <div className="surface rounded-xl p-4">
          <h4 className="font-bold mb-2">السجل الزمني</h4>
          {timeline.length === 0 ? <p className="text-xs text-[#8a8a8a]">لا أحداث مسجلة.</p> : (
            <ol className="space-y-2 text-sm">
              {timeline.map((e, i) => (
                <li key={i} className="flex gap-3 border-b border-[#0a0a0a]/6 pb-2 last:border-0">
                  <span className="text-[#8a6f2e] font-mono text-xs shrink-0">{fmt(e.at)}</span>
                  <span>{e.label_ar}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="surface rounded-xl p-4">
          <h4 className="font-bold mb-2">سجل الإعلانات ({listings.length})</h4>
          {listings.length === 0 ? <p className="text-xs text-[#8a8a8a]">لا إعلانات.</p> : (
            <div className="space-y-2">
              {listings.map((v) => (
                <div key={v.id} className="border border-[#0a0a0a]/8 rounded-lg p-3 text-xs">
                  <div className="flex justify-between font-semibold text-sm">
                    <span>{v.title || v.id}</span>
                    <span className="text-[#8a6f2e]">{v.status || "—"}</span>
                  </div>
                  <div className="text-[#6b6b6b] mt-1 space-y-0.5">
                    <div>نشر: {fmt(v.created_at)}</div>
                    {v.updated_at && v.updated_at !== v.created_at && <div>تعديل: {fmt(v.updated_at)}</div>}
                    {v.deleted_at && <div>حذف: {fmt(v.deleted_at)}</div>}
                    {v.sold_at && <div>بيع: {fmt(v.sold_at)}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="individual-archive">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <User className="w-5 h-5 text-[#8a6f2e]" /> أرشيف المستخدمين الأفراد
        </h2>
        <button onClick={load} disabled={loading} className="btn-ghost text-xs">
          {loading && <Loader2 className="w-3 h-3 animate-spin" />} تحديث
        </button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <input placeholder="بحث بالاسم/البريد/الجوال" value={q} onChange={(e) => setQ(e.target.value)} className="input-dark max-w-xs" />
        <input placeholder="المدينة" value={city} onChange={(e) => setCity(e.target.value)} className="input-dark max-w-xs" />
      </div>
      {error && <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-800">{error}</div>}
      {loading && rows.length === 0 ? (
        <div className="text-[#6b6b6b] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />جاري التحميل…</div>
      ) : rows.length === 0 ? (
        <div className="surface rounded-xl p-8 text-center text-[#6b6b6b]">لا مستخدمون أفراد.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((u) => (
            <div key={u.id} className="surface rounded-xl p-3 flex justify-between items-center hover:border-[#c9a55c] cursor-pointer" onClick={() => open(u.id)} data-testid={`ind-row-${u.id}`}>
              <div>
                <div className="font-semibold">{u.name}</div>
                <div className="text-xs text-[#6b6b6b]">{u.email} · {u.city || "—"} · {u.role}</div>
              </div>
              <div className="text-xs text-[#8a6f2e]">{fmt(u.updated_at || u.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
